/**
 * arbi-dev Engine Atlas — Atlas Day 2 substrate-row content + visual layout.
 *
 * Read-only inspection map of ISG's configured engine state — calculations,
 * judgments, mechanical state, strategic state — surfaced as the position
 * ISG currently asserts about profitability. Engine-only (scenario state
 * lives in buying sessions); admin-only (gated via top-level AdminGate
 * per ADR 37).
 *
 * Scope reference: docs/engine-atlas-v1-scope.md (Sol-locked 4d1bbde).
 * Atlas Day 1 build prompt: docs/build-prompts/atlas-day-1-prompt.md (2115ada).
 * Atlas Day 2 build prompt: docs/build-prompts/atlas-day-2-prompt.md (210467d).
 *
 * Day 2 visual-layout commitment (A.1 + (a)+(b)+(c) + B.3 + C.1 + D + E.1 + F):
 *   - 3×3 grid (3 altitudes × 3 stages) renders cells at grid altitude with
 *     sub-region content visible per A.1; variable-height per occupancy
 *   - Title block + boundary note + ReadingThisMap stay at max-w-5xl
 *   - Grid section breaks max-w-5xl, renders at max-w-7xl per (a)
 *   - Axis labels in left + top grid margins per D — teaches geography
 *     explicitly without UI-convention overhead
 *   - Position row renders quietly at minimal height per E.1 — honors the
 *     "position has its own altitude AND position is synthesized above"
 *     dual fact (E.2 collapse-entirely was rejected at Phase 0 conversation)
 *
 * Day 2 ships substrate-row content via Day-1 RegionContainer placeholders
 * for each cell. Phases 3-4 populate the substrate-row cells with real
 * cluster + flat-region content (Per-entity cluster, Business defaults,
 * Path mechanics, Weight components). Day 2 P2 (this commit) ships the
 * grid + axis labels + position row + cell placeholders so the geometry
 * is visible before content lands.
 *
 * Substrate × calculation cell renders THREE flat regions vertically stacked
 * WITHIN the single cell per scope §4.1 cluster principle (flat-regions
 * mode) and per Atlas Day 2 P0 drift-pattern correction (cell renders 3
 * regions in flat-regions mode; not 3 separate cells which would have
 * broken the locked 3×3 geometry).
 *
 * Transformation-row content + decision-rules cluster + engine calculations
 * region land in Atlas Day 3.
 */

import type { JSX } from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import BoundaryNote from '../components/atlas/BoundaryNote';
import ReadingThisMap from '../components/atlas/ReadingThisMap';
import EmptyCellLabel from '../components/atlas/EmptyCellLabel';
import TitleBlock from '../components/atlas/TitleBlock';
import AtlasGrid from '../components/atlas/AtlasGrid';
import AxisLabel from '../components/atlas/AxisLabel';
import PositionRowCell from '../components/atlas/PositionRowCell';
import PerEntityCluster from '../components/atlas/clusters/PerEntityCluster';
import SubstrateCalcCell from '../components/atlas/regions/SubstrateCalcCell';
import EngineCalculationsRegion from '../components/atlas/regions/EngineCalculationsRegion';
import DecisionRulesCluster from '../components/atlas/clusters/DecisionRulesCluster';

// Scope doc §3 empty-cell copy — verbatim, not paraphrased. Per the
// copy principle (scope doc §4.2), references to internal CF#/ADR# avoided.
const EMPTY_CELL_SUBSTRATE_DECISION_COPY =
  'ISG’s decision rules are currently encoded in engine calculations, not as configurable data. Per-entity rule overrides as data is future scope.';
const EMPTY_CELL_TRANSFORMATION_INPUT_COPY =
  'No standalone input-stage transformations. The engine reads substrate directly into its calculations.';

export default function EngineAtlas(): JSX.Element {
  useDocumentTitle('Engine Atlas');

  return (
    <div className="h-full overflow-auto">
      {/* ── Title + legend section (max-w-5xl per Atlas Day 1 lock) ───── */}
      <div className="max-w-5xl mx-auto p-5 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-ink-bright">Engine Atlas</h1>
        </div>

        <TitleBlock />

        <BoundaryNote />

        <ReadingThisMap />
      </div>

      {/* ── Grid section (max-w-7xl per Atlas Day 2 visual-layout (a)) ── */}
      <div className="max-w-7xl mx-auto px-5 pb-5 space-y-3">
        {/* COMPOSABILITY: top-strip slot for compressed change-history block — wires post-Day 92 */}

        <AtlasGrid>
          {/* Row 1 — axis labels (top margin: stage labels) */}
          <div aria-hidden="true" />
          <AxisLabel axis="horizontal" label="input" />
          <AxisLabel axis="horizontal" label="calculation" />
          <AxisLabel axis="horizontal" label="decision" />

          {/* Row 2 — position altitude (quiet per E.1; no map content) */}
          <AxisLabel axis="vertical" label="position" />
          <PositionRowCell cellCoordinate="position-input" />
          <PositionRowCell cellCoordinate="position-calculation" />
          <PositionRowCell cellCoordinate="position-decision" />

          {/* Row 3 — transformation altitude (Atlas Day 3a P1 populated) */}
          <AxisLabel axis="vertical" label="transformation" />
          <EmptyCellLabel
            cellCoordinate="transformation-input"
            copy={EMPTY_CELL_TRANSFORMATION_INPUT_COPY}
          />
          <EngineCalculationsRegion />
          <DecisionRulesCluster />

          {/* Row 4 — substrate altitude (Atlas Day 2 Phases 3-4 populate cells) */}
          <AxisLabel axis="vertical" label="substrate" />
          <PerEntityCluster />
          {/* Substrate × calculation: ONE cell containing 3 flat regions per
              scope §4.1 cluster principle (flat-regions mode); P4 populated
              with real substrate-driven content */}
          <SubstrateCalcCell />
          <EmptyCellLabel
            cellCoordinate="substrate-decision"
            copy={EMPTY_CELL_SUBSTRATE_DECISION_COPY}
          />
        </AtlasGrid>
      </div>
    </div>
  );
}
